import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Users, MessageSquare, Mail, Phone, ArrowLeft, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return iso;
  }
};

const StatusBadge = ({ value }) => {
  const map = {
    new: 'bg-[#fdecea] text-[#b8331f] border-[#f3c1bb]',
    contacted: 'bg-[#eef7ee] text-[#207a3a] border-[#c9e6cf]',
    archived: 'bg-[#f3ead9] text-[#8f6417] border-[#eadfc9]',
  };
  return (
    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.2em] border ${map[value] || map.new}`}>
      {value}
    </span>
  );
};

const AdminPage = () => {
  const { username, logout } = useAuth();
  const [tab, setTab] = useState('enrolments');
  const [enrolments, setEnrolments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadEnrolments = async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/enrolments?limit=200`);
      setEnrolments(r.data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load enrolments');
    } finally { setLoading(false); }
  };

  const loadSessions = async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/chats/sessions?limit=200`);
      setSessions(r.data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load chats');
    } finally { setLoading(false); }
  };

  const loadMessages = async (id) => {
    setSelectedSession(id); setMessages([]);
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/chats/${encodeURIComponent(id)}`);
      setMessages(r.data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load messages');
    } finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enrolments/${id}?status=${status}`);
      setEnrolments((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      alert('Failed to update status');
    }
  };

  useEffect(() => {
    if (tab === 'enrolments') loadEnrolments();
    if (tab === 'chats') loadSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#fbf7f2] text-[#1c1a17]">
      {/* Header */}
      <div className="bg-[#0f0d0b] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/70 hover:text-white transition-colors flex items-center gap-2 text-[11px] uppercase tracking-[0.28em]">
              <ArrowLeft size={14} /> Back to site
            </Link>
            <span className="text-white/20">|</span>
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#f0c95a] font-serif-display font-black">Whiteplains British School</div>
              <div className="font-serif-display text-xl font-black">Admissions Console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 border border-white/10 p-1">
              <button
                onClick={() => setTab('enrolments')}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.28em] transition-colors flex items-center gap-2 ${tab === 'enrolments' ? 'bg-[#c8942a] text-white' : 'text-white/70 hover:text-white'}`}
              >
                <Users size={13} /> Enrolments
              </button>
              <button
                onClick={() => setTab('chats')}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.28em] transition-colors flex items-center gap-2 ${tab === 'chats' ? 'bg-[#c8942a] text-white' : 'text-white/70 hover:text-white'}`}
              >
                <MessageSquare size={13} /> Chats
              </button>
            </div>
            <div className="hidden lg:flex items-center gap-2 text-[11px] text-white/70">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
              <span className="font-serif-display font-black text-white">{username || 'admin'}</span>
            </div>
            <button
              onClick={logout}
              className="px-3 py-2 text-[11px] uppercase tracking-[0.28em] border border-white/15 hover:border-[#b8331f] hover:bg-[#b8331f] transition-colors flex items-center gap-2 text-white/80 hover:text-white"
              title="Sign out"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif-display text-3xl font-black">
            {tab === 'enrolments' ? 'Enrolment applications' : 'Chat sessions'}
          </h1>
          <button
            onClick={() => (tab === 'enrolments' ? loadEnrolments() : loadSessions())}
            className="btn-outline !py-2 !px-4"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 text-sm text-[#b8331f] bg-[#fdecea] border border-[#f3c1bb] px-4 py-2.5">{error}</div>
        )}

        {/* Enrolments table */}
        {tab === 'enrolments' && (
          <div className="bg-white border border-[#eadfc9] overflow-hidden">
            {enrolments.length === 0 && !loading ? (
              <div className="p-10 text-center text-[#8f6417]">
                <p className="text-sm">No enrolments yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f3ead9] text-[#8f6417]">
                      {['Received', 'Student', 'Parent', 'Programme', 'Contact', 'Status', 'Actions'].map((h) => (
                        <th key={h} className="text-left px-4 py-3 font-sans-pro text-[10px] uppercase tracking-[0.2em]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enrolments.map((e) => (
                      <tr key={e.id} className="border-t border-[#eadfc9] hover:bg-[#fbf7f2] transition-colors">
                        <td className="px-4 py-3 text-[#4a4238] whitespace-nowrap">{fmt(e.created_at)}</td>
                        <td className="px-4 py-3 font-serif-display font-black">{e.student_name}</td>
                        <td className="px-4 py-3 text-[#4a4238]">{e.parent_name}</td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-1 bg-[#f3ead9] text-[#8f6417] text-[11px] border-l-2 border-[#c8942a]">
                            {e.programme}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#4a4238]">
                          <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-[#b8331f] transition-colors">
                            <Mail size={13} /> {e.email}
                          </a>
                          <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 hover:text-[#b8331f] transition-colors mt-1">
                            <Phone size={13} /> {e.phone}
                          </a>
                        </td>
                        <td className="px-4 py-3"><StatusBadge value={e.status} /></td>
                        <td className="px-4 py-3">
                          <select
                            value={e.status}
                            onChange={(ev) => updateStatus(e.id, ev.target.value)}
                            className="wbs-input !py-1.5 !px-2 !text-xs !w-auto"
                          >
                            <option value="new">new</option>
                            <option value="contacted">contacted</option>
                            <option value="archived">archived</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Chats view */}
        {tab === 'chats' && (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-[#eadfc9] md:col-span-1 max-h-[70vh] overflow-y-auto">
              {sessions.length === 0 ? (
                <div className="p-8 text-center text-[#8f6417] text-sm">No chats yet.</div>
              ) : (
                sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => loadMessages(s.session_id)}
                    className={`w-full text-left p-4 border-b border-[#eadfc9] hover:bg-[#fbf7f2] transition-colors ${selectedSession === s.session_id ? 'bg-[#fbf7f2]' : ''}`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-serif-display font-black text-sm truncate">{s.session_id.slice(0, 18)}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8f6417]">{s.count} msg</div>
                    </div>
                    <div className="text-[12px] text-[#4a4238] mt-1 line-clamp-2">{s.last_text}</div>
                    <div className="text-[10px] text-[#8f6417] mt-1">{fmt(s.last_time)}</div>
                  </button>
                ))
              )}
            </div>
            <div className="bg-white border border-[#eadfc9] md:col-span-2 min-h-[50vh] flex flex-col">
              {!selectedSession ? (
                <div className="flex-1 grid place-items-center text-[#8f6417] text-sm">Select a session to view messages</div>
              ) : (
                <>
                  <div className="p-4 border-b border-[#eadfc9] font-serif-display font-black text-sm">Session · {selectedSession}</div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#fbf7f2]">
                    {messages.map((m) => (
                      <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] px-3.5 py-2 text-sm rounded-lg ${m.sender === 'user' ? 'bg-[#0f0d0b] text-white' : 'bg-white border border-[#eadfc9]'}`}>
                          <div>{m.text}</div>
                          <div className="mt-1 text-[10px] opacity-60 uppercase tracking-[0.2em]">{fmt(m.created_at)}</div>
                        </div>
                      </div>
                    ))}
                    {messages.length === 0 && !loading && <div className="text-center text-[#8f6417] text-sm">No messages</div>}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
